import { BrowseTickers } from "@aitisal/contracts/restaurant/brand";
import { browseTickers } from "@aitisal/sdk/restaurant";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { getTitle } from "../helper/helper";
import { useNotificationWindow } from "../stores/stores";
import { useBrandingConfig } from "../utils/brandingConfig";

interface NewsTickerProps {
  autoScroll?: boolean;
}

export function NewsTicker({ autoScroll = true }: NewsTickerProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [containerWidth, setContainerWidth] = useState(0);
  const [textWidth, setTextWidth] = useState(0);
  const [isMultiLine, setIsMultiLine] = useState(false);
  const textRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const { setTickerCount } = useNotificationWindow();

  const { brandId } = useBrandingConfig();

  const { data: newsData } = useQuery<BrowseTickers>({
    queryKey: ["news", brandId],
    queryFn: () => browseTickers(brandId || "").then((res) => res.data),
    enabled: Boolean(brandId),
  });

  useEffect(() => {
    if (!autoScroll || (newsData?.items?.length || 0) <= 1) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % (newsData?.items?.length || 0));
    }, 5000);

    return () => clearInterval(interval);
  }, [autoScroll, newsData?.items?.length]);

  useEffect(() => {
    if (newsData?.items?.length) {
      setTickerCount(newsData?.items?.length || 0);
    } else if (newsData?.items?.length === 0) {
      setTickerCount(0);
    }
  }, [newsData?.items?.length]);

  const currentNews = newsData?.items?.[currentIndex];

  useEffect(() => {
    if (textRef.current && containerRef.current) {
      const el = textRef.current;
      const containerEl = containerRef.current;
      setIsOverflowing(el.scrollWidth > containerEl.clientWidth);
      setTextWidth(el.scrollWidth);
      setContainerWidth(containerEl.clientWidth);
      setIsMultiLine(el.scrollHeight > el.clientHeight + 1);
    }
  }, [currentNews, newsData?.items?.length, currentIndex, containerWidth, textWidth]);

  useEffect(() => {
    function handleResize() {
      if (textRef.current && containerRef.current) {
        const el = textRef.current;
        const containerEl = containerRef.current;
        setIsOverflowing(el.scrollWidth > containerEl.clientWidth);
        setTextWidth(el.scrollWidth);
        setContainerWidth(containerEl.clientWidth);
        setIsMultiLine(el.scrollHeight > el.clientHeight + 1);
      }
    }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  if (!newsData?.items || newsData?.items.length === 0) return null;
  if (!currentNews) return null;

  const baseDuration = 10;
  const minDuration = 6;
  const maxDuration = 24;
  let marqueeDuration = baseDuration;
  if ((isOverflowing || isMultiLine) && textWidth && containerWidth) {
    marqueeDuration = Math.max(
      minDuration,
      Math.min(maxDuration, (textWidth + containerWidth) / 100),
    );
  }

  const shouldMarquee = isMultiLine || isOverflowing;

  const getTypeConfig = (type: string) => {
    switch (type) {
      case "success":
        return { label: "Offer", dotColor: "bg-emerald-400" };
      case "info":
        return { label: "Info", dotColor: "bg-sky-400" };
      case "warning":
        return { label: "Alert", dotColor: "bg-amber-400" };
      case "announcement":
      default:
        return { label: "New", dotColor: "bg-white" };
    }
  };

  const typeConfig = getTypeConfig(currentNews?.type || "");

  let marqueeAnimStyle: React.CSSProperties = {};
  let marqueeKeyframes = "";

  if (shouldMarquee && textWidth && containerWidth && !isMultiLine) {
    const distance = textWidth - containerWidth;
    marqueeAnimStyle = {
      whiteSpace: "nowrap",
      display: "inline-block",
      position: "absolute",
      left: 0,
      top: 0,
      willChange: "transform",
      animation: `marquee-slide-custom ${marqueeDuration}s linear infinite`,
      width: "auto",
    };
    marqueeKeyframes = `
      @keyframes marquee-slide-custom {
        0% { transform: translateX(0); }
        100% { transform: translateX(-${distance}px); }
      }
    `;
  } else if (shouldMarquee && isMultiLine) {
    marqueeAnimStyle = {
      whiteSpace: "normal",
      display: "inline-block",
      position: "absolute",
      left: 0,
      top: 0,
      willChange: "transform",
      animation: `marquee-slide-multiline ${marqueeDuration}s linear infinite`,
      width: "100%",
    };
    marqueeKeyframes = `
      @keyframes marquee-slide-multiline {
        0% { transform: translateX(0); }
        100% { transform: translateX(-100%); }
      }
    `;
  }

  return (
    <div className="w-full bg-[var(--brand-color)] text-white transition-all duration-300">
      <div className="max-w-7xl mx-auto px-4 py-2.5">
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0 flex items-center gap-1.5 px-2.5 py-1 bg-white/15 backdrop-blur-sm rounded text-[11px] font-bold uppercase tracking-[0.08em]">
            <span className={`w-1.5 h-1.5 rounded-full ${typeConfig.dotColor}`} />
            <span>{typeConfig.label}</span>
          </div>
          <div className="flex-1 overflow-hidden" ref={containerRef}>
            <div
              className="relative"
              style={{
                minWidth: 0,
                width: "100%",
                height: "22px",
                lineHeight: "22px",
              }}
            >
              {shouldMarquee ? (
                <div className="absolute inset-0 overflow-hidden">
                  <div
                    ref={textRef}
                    className="marquee-anim text-[13px] font-medium text-white/90"
                    style={{
                      ...marqueeAnimStyle,
                      lineHeight: "22px",
                    }}
                  >
                    {getTitle(currentNews?.message) || ""}
                  </div>
                </div>
              ) : (
                <div
                  ref={textRef as any}
                  className="text-[13px] font-medium text-white/90 h-full flex items-center"
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {getTitle(currentNews?.message) || ""}
                </div>
              )}
            </div>
          </div>
          {(newsData?.items?.length || 0) > 1 && (
            <div className="flex gap-1 flex-shrink-0 items-center">
              {newsData?.items?.map((_, index) => (
                <div
                  key={index}
                  className={`h-1 rounded-full transition-all duration-500 ${
                    index === currentIndex
                      ? "w-5 bg-white"
                      : "w-1 bg-white/30"
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
      <style>{`
        .marquee-anim { will-change: transform; }
        ${marqueeKeyframes}
      `}</style>
    </div>
  );
}
